import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import { useAuth } from '../contexts/AuthContext';
import { Store } from '../contexts/StoreContext';
import { getPendingStoreRequests, acceptStoreRequest, rejectStoreRequest } from '../services/storeService';
import { ApiErrorResponse } from '../services/authService';
import Pagination from '../components/common/Pagination';
import styles from './StoreRequestsPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import layoutStyles from '../styles/layout.module.css';
import Alert from '../components/common/Alert';
import placeholderImage from '../assets/placeholder.png';

const DEFAULT_LIMIT = 20;

const StoreRequestsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [pendingStores, setPendingStores] = useState<Store[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  const fetchPendingStores = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getPendingStoreRequests({ page, limit, search: search || undefined, sortBy, sortOrder });
      setPendingStores(result.items);
      setTotal(result.total);
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      setError(apiError.message || 'Failed to load pending store requests.');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, page, limit, search, sortBy, sortOrder]);

  useEffect(() => {
    fetchPendingStores();
  }, [fetchPendingStores]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  const handleAccept = async (storeId: number) => {
    if (!window.confirm('Are you sure you want to accept this store request?')) {
      return;
    }

    hideAlert();
    try {
      await acceptStoreRequest(storeId);
      setPendingStores((prev) => prev.filter(store => store.id !== storeId));
      setTotal((t) => Math.max(0, t - 1));
      showAlert('Store request accepted successfully!', 'success');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || 'Failed to accept store request.', 'error');
    }
  };

  const handleReject = async (storeId: number) => {
    if (!window.confirm('Are you sure you want to reject this store request? This will delete the store permanently.')) {
      return;
    }

    hideAlert();
    try {
      await rejectStoreRequest(storeId);
      setPendingStores((prev) => prev.filter(store => store.id !== storeId));
      setTotal((t) => Math.max(0, t - 1));
      showAlert('Store request rejected and deleted successfully.', 'success');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || 'Failed to reject store request.', 'error');
    }
  };

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.errorContainer}>
              <p>Access denied. Only administrators can view store requests.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && pendingStores.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading pending store requests...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <div className={styles.headerSection}>
            <h1 className={styles.pageTitle}>Store Requests</h1>
            <p className={styles.pageSubtitle}>Review and manage pending store requests</p>
          </div>

          <Alert
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />

          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.toolbar}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search stores..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.searchInput}
              />
              <button type="submit" className={styles.searchButton}>Search</button>
            </form>
            <label className={styles.sortLabel}>
              Sort by:
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                  setSortBy(by);
                  setSortOrder(order);
                  setPage(1);
                }}
                className={styles.sortSelect}
              >
                <option value="id-desc">ID (newest)</option>
                <option value="id-asc">ID (oldest)</option>
                <option value="store_name-asc">Name (A–Z)</option>
                <option value="store_name-desc">Name (Z–A)</option>
              </select>
            </label>
          </div>

          {pendingStores.length === 0 && !isLoading ? (
            <div className={styles.emptyState}>
              <p>No pending store requests at this time.</p>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={tableStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Store Name</th>
                    <th>Description</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Location</th>
                    <th>Categories</th>
                    <th>Requested By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingStores.map((store) => (
                    <tr key={store.id}>
                      <td className={tableStyles.imageCell}>
                        {store.image_url ? (
                          <img
                            src={store.image_url}
                            alt={store.store_name}
                            className={tableStyles.tableImage}
                            onError={(e) => {
                              e.currentTarget.src = placeholderImage;
                            }}
                          />
                        ) : (
                          <div className={tableStyles.tablePlaceholder}>🏪</div>
                        )}
                      </td>
                      <td>{store.store_name}</td>
                      <td className={styles.descriptionCell}>{store.store_description}</td>
                      <td>{store.email}</td>
                      <td>{store.mobile_number || 'N/A'}</td>
                      <td>
                        {store.subLocation?.location?.location_name && store.subLocation?.sub_location_name
                          ? `${store.subLocation.location.location_name} - ${store.subLocation.sub_location_name}`
                          : 'N/A'}
                      </td>
                      <td>
                        {store.categories && store.categories.length > 0
                          ? store.categories.map((cat) => cat.category_name).join(', ')
                          : 'N/A'}
                      </td>
                      <td>
                        {store.user
                          ? `${store.user.first_name || ''} ${store.user.last_name || ''}`.trim() || store.user.username
                          : 'N/A'}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            onClick={() => handleAccept(store.id)}
                            className={`${styles.actionButton} ${styles.acceptButton}`}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleReject(store.id)}
                            className={`${styles.actionButton} ${styles.rejectButton}`}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
              limitOptions={[10, 20, 50]}
              showLimitSelector
              disabled={isLoading}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default StoreRequestsPage;

